import React from "react";

/**
 * A simple and custom markdown-to-JSX formatter.
 *
 * This function is safer than a string-based approach because it returns React elements,
 * eliminating the risk of XSS attacks by avoiding `dangerouslySetInnerHTML`.
 *
 * It's designed to handle a specific format:
 * 1. Bolding with **text**.
 * 2. Line jumps with `\n`.
 * 3. An introductory paragraph followed by a list of items separated by " - ".
 *
 * @param text The markdown-like string to format.
 * @returns A React Node (`JSX.Element`) or `null`.
 */

// This helper function takes a string and converts it into an array of strings and React elements.
const applyFormatting = (str: string): React.ReactNode[] => {
  // Split the string by our target tokens (bold markers and newlines) while keeping them.
  const parts = str.split(/(\*\*.*?\*\*|\n)/g).filter(Boolean); // filter(Boolean) removes empty strings

  return parts.map((part, index) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      // It's a bold part, return a <strong> element.
      return <strong key={index}>{part.slice(2, -2)}</strong>;
    }
    if (part === "\n") {
      // It's a newline, return a <br /> element.
      return <br key={index} />;
    }
    // Otherwise, it's just a regular string.
    return part;
  });
};

export const formatCustomMarkdownToJsx = (text: string): React.ReactNode => {
  if (!text) {
    return null;
  }

  // The custom format uses " - " as a separator for list-like items.
  const parts = text.split(" - ");

  if (parts.length > 1) {
    // The first part is treated as an introduction.
    const intro = <p key="intro">{applyFormatting(parts[0])}</p>;

    // The subsequent parts are treated as list items.
    const listItems = parts
      .slice(1)
      .map((item, index) => <li key={index}>{applyFormatting(item.trim())}</li>);

    // We use a React Fragment to return multiple elements.
    return (
      <>
        {intro}
        <ul key="list">{listItems}</ul>
      </>
    );
  } else {
    // If there are no separators, treat the entire text as a single paragraph.
    return <p>{applyFormatting(text)}</p>;
  }
};
